import { Amplify } from 'aws-amplify';
import {
	signIn,
	signOut,
	getCurrentUser,
	fetchAuthSession,
	confirmSignIn,
	resetPassword,
	confirmResetPassword,
	updatePassword,
	type AuthSession,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import axios from 'axios';
import JwtDecode from 'jwt-decode';
import QueryString from 'query-string';

import { PATHS } from '../AppDomains';
import { getEmbeddedLaunchDestinationFullPathOrNull } from '../authentication/embeddedLaunchDestination';
import {
	API_URL_SUMMARY_LAMBDAS,
	AUTH0_CLIENT_ID,
	AUTH0_LOGIN_REDIRECT,
	AUTH0_LOGIN_REDIRECT_BASE_URL,
	COGNITO_APP_CLIENT_ID,
	COGNITO_COOKIE_DOMAIN,
	COGNITO_USER_POOL_ID,
	getMaybeAuthServiceAuth0TokenUrl,
	USE_AUTH_SERVICE,
} from '../config/Environment';
import { isPartOfEPICLoginFlow } from '../pages/epic/epicLoginFlowFlag';
import { preloadSummaryLandingPage } from '../pages/summary/SummaryLandingPage.loader';
import {
	getEmbeddedOverlayLegacyAppointmentPortraitPath,
	getEmbeddedPatientPortraitPath,
} from '../react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes.utils';
import { fetchUserMetadata } from '../server/fetchUserMetadata';
import { identityProviders } from '../types';
import { callTrackingApplicationLoadTime } from './applicationTimeTracking'; // oxlint-disable-line import/no-cycle -- Pre-existing dependency cycle, grandfathered during the oxlint migration.
import { addCurrentTimeUnixToLocalStorage } from './common';
import {
	AUTH_0_TOKEN,
	CREATE_VERADIGM_UNITY_JWT_TOKEN,
	EMR_USERNAME_KEY,
	NEXT_URL_QUERY_PARAMS,
	REQUEST_TIME_GET_USER_META_DATA,
	REQUEST_TO_AUTH0_TOKEN_TIME,
	RESPONSE_FROM_AUTH0_TOKEN_TIME,
	RESPONSE_TIME_GET_USER_META_DATA,
	TOKEN_EXP_TIME,
	UNITY_ENCOUNTER_ID,
	USER_METADATA,
} from './consts';
import { EpicFhirCache } from './epicFhirCache';
import { getOrCreateLogger } from './logger'; // oxlint-disable-line import/no-cycle -- Pre-existing dependency cycle, grandfathered during the oxlint migration.
import { getLoggableError } from './logger.auth.utils';

import type { NavinaPermissions } from '../stores/authStore';
import type { UserMetadataResponsePayload } from '@navina/api-types';

const logger = getOrCreateLogger();

// Initialize Amplify configuration
function initializeAmplify(): void {
	Amplify.configure({
		Auth: {
			Cognito: {
				userPoolId: COGNITO_USER_POOL_ID,
				userPoolClientId: COGNITO_APP_CLIENT_ID,
				loginWith: {
					email: true,
				},
			},
		},
	});

	// order matters
	cognitoUserPoolsTokenProvider.setKeyValueStorage(
		new CookieStorage({
			domain: COGNITO_COOKIE_DOMAIN,
			path: '/',
			expires: 30,
			sameSite: 'none',
			secure: true,
		}),
	);
}

type AuthDataCallback = (
	username: string | null,
	session: AuthSession | null,
	auth0Token: string,
	userMetadata: UserMetadata | null,
	isSSO?: boolean,
) => void;

export interface UserMetadata extends Pick<UserMetadataResponsePayload, 'navinaAppointmentId' | 'navinaPatientId'> {
	readonly analyticsMetadata: Record<string, string>;
	readonly permissions: NavinaPermissions;
	readonly emrUsername: string;
	readonly emrName: string;
	readonly isAdmin: boolean;
	readonly navinaUsername: string;
	readonly sid?: string;
	readonly dataSourceId?: string;
}

interface Auth0TokenPayload {
	code: string;
	redirectUri: string;
	clientId: string;
}

interface UnityTokenPayload {
	ssoToken: string;
}

// TODO - Can we extend the existing NavinaJwt type instead of creating a new one?

interface Auth0VeradigmUnityJwtToken {
	readonly 'https://navina.ai/unity_patient_id'?: string;
	readonly 'https://navina.ai/unity_encounter_id'?: string;
}

interface Auth0AthenaJwtToken {
	readonly 'https://navina.ai/emr_encounter_id'?: string;
	readonly 'https://navina.ai/emr_encounter_fhir_id'?: string;
	readonly 'https://navina.ai/emr_patient_id'?: string;
}

export interface Auth0JwtToken extends Auth0VeradigmUnityJwtToken, Auth0AthenaJwtToken {
	readonly aud: string;
	readonly exp: number;
	readonly 'https://navina.ai/dataSourceId': string;
	readonly 'https://navina.ai/dataSourceIds'?: ReadonlyArray<string>;
	readonly 'https://navina.ai/email': string;
	readonly 'https://navina.ai/doc_id'?: string;
	readonly iat: number;
	readonly iss: string;
	readonly sid: string;
	readonly sub: string;
}

export enum SignInStatus {
	initial,
	success,
	firstLoginPasswordChange,
	oldPassword,
	oldLogin,
	failure,
	totpRequired,
	totpFailure,
	credentialsFailure,
	passwordAttemptsExceeded,
	recoverPasswordRequest,
	generalError,
}

const savingTokenRelevantDataToLocalStorage = (token: string, decodedToken: Auth0JwtToken): void => {
	localStorage.setItem(AUTH_0_TOKEN, token);
	localStorage.setItem(TOKEN_EXP_TIME, String(decodedToken.exp));
};

const saveNextUrlToLocalStorage = (nextUrl: string): void => {
	localStorage.setItem(NEXT_URL_QUERY_PARAMS, nextUrl);
};

interface GetEpicLoginNextUrlFromMetadataParams {
	readonly maybeNavinaPatientId: UserMetadataResponsePayload['navinaPatientId'];
	readonly maybeDocumentId: Auth0JwtToken['https://navina.ai/doc_id'];
	readonly maybeNavinaAppointmentId: UserMetadataResponsePayload['navinaAppointmentId'];
}

function getEpicLoginNextUrlFromMetadataOrNull({
	maybeNavinaPatientId,
	maybeDocumentId,
	maybeNavinaAppointmentId,
}: GetEpicLoginNextUrlFromMetadataParams): string | null {
	const { search } = globalThis.location;
	const embeddedLaunchDestinationPath = getEmbeddedLaunchDestinationFullPathOrNull(search);
	if (embeddedLaunchDestinationPath !== null) {
		return embeddedLaunchDestinationPath;
	}

	if (maybeNavinaPatientId) {
		return getEmbeddedPatientPortraitPath({
			navinaPatientId: maybeNavinaPatientId,
			navinaAppointmentId: maybeNavinaAppointmentId ?? undefined,
		});
	}

	if (maybeDocumentId) {
		return `/document/${maybeDocumentId}`;
	}

	if (maybeNavinaAppointmentId) {
		return getEmbeddedOverlayLegacyAppointmentPortraitPath({
			navinaAppointmentId: maybeNavinaAppointmentId,
		});
	}

	return null;
}

const saveRelevantUserMetadataToLocalStorage = (userMetadata: UserMetadata): void => {
	localStorage.setItem(USER_METADATA, JSON.stringify(userMetadata));
	localStorage.setItem(EMR_USERNAME_KEY, userMetadata.emrUsername);
};

function checkIsAuth0Login(auth0Code: string): boolean {
	return (
		Boolean(auth0Code) &&
		!window.location.pathname.includes('epic') &&
		!window.location.pathname.includes(PATHS.SSO_CALLBACK_PATH)
	);
}

interface AuthArgs {
	readonly setIsInitializing: (isInitializing: boolean) => void;
}

export class Auth {
	private readonly isInitialized: boolean = false;
	private readonly setIsInitializing: (isInitializing: boolean) => void;

	constructor({ setIsInitializing }: AuthArgs) {
		this.setIsInitializing = setIsInitializing;

		// Check if Amplify is initialized, if not, initialize it.
		if (!this.isInitialized) {
			initializeAmplify();
			this.isInitialized = true;
		}
	}

	/** Sign in and authenticate a user
	 * Scenarios possible:
	 * - Successful sign in (username passoword)
	 * - TOTP required
	 * - Error: Password change required
	 * - Error: General error
	 * - Error: Login too old
	 * - Error: Wrong verification code
	 * - Error: Password reset required
	 * - Error: Incorrect username or password
	 * - Error: Password attempts exceeded
	 */
	readonly signIn = async (
		email: string,
		password: string,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
		verificationCode?: string,
	): Promise<void> => {
		logger.trace('Sign-in: attempt started', { username: email });

		try {
			const result = await signIn({
				username: email,
				password: password,
			});

			if (result.isSignedIn) {
				await this.handleSuccessfulSignIn(email, afterLoginCallback);
			} else if (
				// be able to handle TOTP and OTP email code
				result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
				result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
			) {
				await this.handleTotpRequired(email, verificationCode, afterLoginCallback);
			} else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
				this.handlePasswordChangeRequired(afterLoginCallback);
			} else {
				afterLoginCallback?.(null, null, SignInStatus.failure);
			}
		} catch (err: any) {
			const errorHandled = await this.handleSignInError(err, email, password, afterLoginCallback, verificationCode);

			if (!errorHandled) {
				logger.error('Sign-in: unhandled error', { username: email, error: getLoggableError(err) });
				afterLoginCallback?.(null, null, SignInStatus.failure);
			}
		}
	};

	// Send MFA code to the user
	readonly sendMFA = async (
		verificationCode: string,
		callback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
	): Promise<void> => {
		try {
			// Confirm the sign in with the MFA code. this happens after the user has entered username and password + MFA code.
			await confirmSignIn({
				challengeResponse: verificationCode,
			});

			// Fetch the session after the sign in is confirmed. this also automatically refreshes the token.
			const session = await fetchAuthSession();
			// Call the callback with the session and the status.
			callback?.(null, session, SignInStatus.success);
		} catch (err: any) {
			if (err.name === 'CodeMismatchException' && err.message === 'Invalid code received for user') {
				logger.trace('MFA: wrong verification code');
				callback?.(null, null, SignInStatus.totpFailure);
			} else {
				logger.error('MFA: confirmation failed', { error: getLoggableError(err) });
				callback?.(null, null, SignInStatus.totpFailure);
			}
		}
	};

	// Refresh the user's session
	readonly refresh = async (
		callback?: (email: string | null, session: AuthSession | null, status?: SignInStatus) => void,
	): Promise<string> => {
		try {
			const user = await getCurrentUser();
			const session = await fetchAuthSession({ forceRefresh: true });

			if (session && user) {
				callback?.(user.username, session);
				return '';
			}

			console.error('Session refresh: no valid session after refresh');
			callback?.(null, null);
			throw new Error('No valid session');
		} catch (err) {
			console.warn('Session refresh: no Cognito user, probably never logged in', { error: getLoggableError(err) });
			callback?.(null, null);
			throw err;
		}
	};

	readonly refreshToken = async (): Promise<AuthSession> => {
		try {
			// Fetch the session and refresh the token. force refresh the token even if its not expired.
			const session = await fetchAuthSession({ forceRefresh: true });
			if (session) {
				return session;
			}
			throw new Error('No valid session');
		} catch (err) {
			console.error('Token refresh: failed', { error: getLoggableError(err) });
			throw err;
		}
	};

	readonly signOut = async (isSaml = false): Promise<void> => {
		try {
			await signOut();
			logger.setUsername(undefined);

			if (isSaml) {
				localStorage.setItem('isSaml', 'true');
				localStorage.removeItem(AUTH_0_TOKEN);
				globalThis.location.href = `${AUTH0_LOGIN_REDIRECT_BASE_URL}v2/logout?returnTo=${AUTH0_LOGIN_REDIRECT}&client_id=${AUTH0_CLIENT_ID}`;
			}
		} catch (err) {
			console.error('Sign-out: failed', { error: getLoggableError(err) });
		}
	};

	readonly completeNewPasswordChallenge = async (newPassword: string, successCallback: VoidFunction): Promise<void> => {
		try {
			logger.trace('New password challenge: started');
			await confirmSignIn({
				challengeResponse: newPassword,
			});
			await signOut();
			logger.trace('New password challenge: completed, signed out');
			successCallback();
		} catch (err) {
			logger.error('New password challenge: failed', { error: getLoggableError(err) });
			throw err;
		}
	};

	readonly requestPasswordReset = async (username: string): Promise<string> => {
		try {
			await resetPassword({ username });
			logger.trace('Password reset: requested', { username });
			return 'requestPasswordReset';
		} catch (err) {
			logger.error('Password reset: request failed', { username, error: getLoggableError(err) });
			throw err;
		}
	};

	// Confirm the forgot password email code and set the new password.
	// This will not work for email MFA. since cognito doesn't allow it.
	// The workaround is via admin Tool.
	readonly confirmForgotPassword = async (
		username: string,
		newPassword: string,
		emailCode: string,
		onSuccess: (succeed: boolean) => void,
		onFailure: (error: Error) => void,
	): Promise<void> => {
		try {
			await confirmResetPassword({
				username,
				confirmationCode: emailCode,
				newPassword,
			});

			logger.trace('Password reset: confirmed', { username });
			onSuccess(true);
		} catch (err: any) {
			logger.error('Password reset: confirmation failed', { username, error: getLoggableError(err) });
			onFailure(err);
		}
	};

	readonly changePassword = async (
		oldPassword: string,
		newPassword: string,
		callback: (err: any, result: any) => void,
	): Promise<void> => {
		try {
			await updatePassword({
				oldPassword,
				newPassword,
			});
			logger.trace('Password change: succeeded');
			callback(null, 'success');
		} catch (err: any) {
			logger.error('Password change: failed', { error: getLoggableError(err) });
			const errorMessage = err?.message || 'Error changing password';
			callback(errorMessage, null);
		}
	};

	readonly getAuthData = async (callback: AuthDataCallback): Promise<void> => {
		const usingSSO = this.handleAuthData(callback);
		if (!usingSSO) {
			await this.handleCognitoAuthData(callback);
		}
	};

	// Completes the SSO popup auth flow: exchanges the Auth0 authorization code for a token using
	// the popup's redirect_uri (/sso/callback), which must match the original authorization request.
	readonly completeSSOPopupLogin = (code: string, callback: AuthDataCallback): void => {
		const redirectUri = `${globalThis.location.origin}${PATHS.SSO_CALLBACK_PATH}`;
		this.setIsInitializing(true);
		void this.handleTokenRetrieval(AUTH_0_TOKEN, { code, redirectUri, clientId: AUTH0_CLIENT_ID }, callback);
	};

	// newer versions of aws amplify do not expose directly refresh tokens.
	// it does however keep it on the browser. this is a workaround to access the refresh token
	// WARNING: This method depends on aws-amplify version 6.0.0 (see package.json).
	// Do not change the aws-amplify version without testing this method, as it relies on
	// Amplify v6's specific cookie key pattern for refresh tokens.
	readonly getRefreshTokenFromCookie = async (): Promise<string | null> => {
		const user = await getCurrentUser();
		if (!user?.username) {
			throw new Error('No user found');
		}

		// Amplify v6 stores tokens in cookies (via CookieStorage). Try to access the refresh token from cookies.
		// The key pattern used by Amplify is typically: CognitoIdentityServiceProvider.{clientId}.{username}.refreshToken
		// Note: The username in the cookie name is URL-encoded (e.g., @ becomes %40)
		const encodedUsername = encodeURIComponent(user.username);
		const cookieKey = `CognitoIdentityServiceProvider.${COGNITO_APP_CLIENT_ID}.${encodedUsername}.refreshToken`;

		// Parse document.cookie to find the refresh token cookie
		const cookies = document.cookie.split('; ');
		for (const cookie of cookies) {
			const [name, value] = cookie.split('=');
			if (name === cookieKey && value) {
				// Decode the cookie value in case it's URL-encoded
				return decodeURIComponent(value);
			}
		}
		return null;
	};

	private readonly handleCognitoAuthData = async (callback: AuthDataCallback): Promise<void> => {
		try {
			console.log('Cognito init: checking for signed-in user');
			// first check if user is in cognito
			let userInCognito = true;
			let username: string | null = null;

			try {
				const user = await getCurrentUser();
				if (user?.username) {
					username = user.username;
				} else {
					userInCognito = false;
				}
			} catch (error) {
				console.log('Cognito init: no current user', { error: getLoggableError(error) });
				userInCognito = false;
			}

			if (!userInCognito) {
				logger.trace('Cognito init: user not logged in');
				callback(null, null, '', null);
			} else {
				// @ts-expect-error - Legacy code, nullability not yet enforced
				logger.setUsername(username);
				try {
					const session = await fetchAuthSession();
					if (session) {
						console.log('Cognito init: user already logged in');
						callback(username, session, '', null);
					} else {
						console.log('Cognito init: user has no session');
						callback(null, null, '', null);
					}
				} catch (err) {
					console.error('Cognito init: failed to fetch auth session', { error: getLoggableError(err) });
					callback(null, null, '', null);
				}
			}
		} catch (error) {
			console.error('Cognito init: unexpected error', { error: getLoggableError(error) });
			callback(null, null, '', null);
		}
	};

	private readonly handleTokenRetrieval = async (
		apiEndpoint: string,
		payload: Auth0TokenPayload | UnityTokenPayload,
		callback: AuthDataCallback,
	): Promise<void> => {
		try {
			const trackingEpicFLow = isPartOfEPICLoginFlow();
			if (trackingEpicFLow) {
				addCurrentTimeUnixToLocalStorage(REQUEST_TO_AUTH0_TOKEN_TIME);
			}
			const maybeAuthServiceUrl =
				apiEndpoint === AUTH_0_TOKEN && USE_AUTH_SERVICE === 'true' ? getMaybeAuthServiceAuth0TokenUrl() : null;
			const path = maybeAuthServiceUrl ?? `${API_URL_SUMMARY_LAMBDAS}${apiEndpoint}`;
			const response = await axios.post(path, payload, {
				headers: { 'content-type': 'application/json' },
			});

			if (trackingEpicFLow) {
				addCurrentTimeUnixToLocalStorage(RESPONSE_FROM_AUTH0_TOKEN_TIME);
			}

			console.log('Token retrieval: token received, processing');

			await this.handleAuth0Token(response.data.jwtToken || response.data.id_token, callback);
		} catch (error) {
			logger.error('Token retrieval or processing: failed', { apiEndpoint, error: getLoggableError(error) });
		}
	};

	// Initialize the SSO auth process
	private readonly initializeAuthProcess = (
		authType: identityProviders,
		auth0Code: string,
		unitySSOToken: string,
		callback: AuthDataCallback,
	): void => {
		const apiEndpoint = authType === identityProviders.Navina ? CREATE_VERADIGM_UNITY_JWT_TOKEN : AUTH_0_TOKEN;
		const payload =
			authType === identityProviders.Navina
				? { ssoToken: unitySSOToken }
				: {
						code: auth0Code,
						redirectUri: AUTH0_LOGIN_REDIRECT,
						clientId: AUTH0_CLIENT_ID,
					};

		const paramToRemove = authType === identityProviders.Navina ? 'sso' : 'code';
		this.removeUrlParam(paramToRemove);

		this.setIsInitializing(true);

		void this.handleTokenRetrieval(apiEndpoint, payload, callback);
	};

	private readonly handleAuthData = (callback: AuthDataCallback): boolean => {
		console.log('SSO init: checking for SSO login');

		// In the Epic embedded flow the destination is always a patient portrait / summary page.
		// Start downloading that lazy chunk (which contains the apiGateway) now, so it is ready by
		// the time the auth/token/metadata/config round-trips finish, instead of loading it serially
		// on the critical path once the route mounts.
		if (isPartOfEPICLoginFlow()) {
			void preloadSummaryLandingPage();
		}

		const queryParams = QueryString.parse(window.location.search);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Legacy code
		const auth0Code = queryParams.code as string;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Legacy code
		const unitySSOToken = queryParams.sso as string;

		const isUnityLogin = Boolean(unitySSOToken) && !window.location.pathname.includes('veradigm');
		const isAuth0Login = checkIsAuth0Login(auth0Code);

		if (isAuth0Login && isPartOfEPICLoginFlow()) {
			callTrackingApplicationLoadTime();
		}

		if (isUnityLogin || isAuth0Login) {
			console.log(`SSO init: starting ${isUnityLogin ? 'Unity' : 'Auth0'} login`);
			const authType = isUnityLogin ? identityProviders.Navina : identityProviders.Auth0;
			this.initializeAuthProcess(authType, auth0Code, unitySSOToken, callback);
			return true;
		}

		// Check if user logged in with SSO before
		const auth0Token = localStorage.getItem(AUTH_0_TOKEN);
		if (auth0Token) {
			console.log('SSO init: found cached Auth0 token');

			const emrUsername = localStorage.getItem(EMR_USERNAME_KEY);
			// @ts-expect-error - Legacy code, nullability not yet enforced
			const userMetadata: UserMetadata = JSON.parse(localStorage.getItem(USER_METADATA));
			callback(emrUsername, null, auth0Token, userMetadata, true);
			return true;
		}

		console.log('SSO init: not an SSO login, falling back to Cognito');
		// @ts-expect-error - Legacy code, nullability not yet enforced
		callback(null, null, null, null);
		return false;
	};

	private readonly refreshEpicFhirAuthCacheIfNeeded = (
		emrEncounterFhirId: string | undefined,
		summaryId: string | null,
	): void => {
		if (emrEncounterFhirId && summaryId) {
			EpicFhirCache.removeAllAndUpdate({ summaryId, data: { emrEncounterFhirId } });
		}
	};

	private readonly handleAuth0Token = async (idToken: string, callback: AuthDataCallback): Promise<void> => {
		const decodedIdToken = JwtDecode<Auth0JwtToken>(idToken);
		const dataSourceId = decodedIdToken['https://navina.ai/dataSourceId'];
		const possibleDataSourceIds = decodedIdToken['https://navina.ai/dataSourceIds'];
		const email = decodedIdToken['https://navina.ai/email'];
		const epicEmrEncounterId = decodedIdToken?.['https://navina.ai/emr_encounter_id'];
		const emrEncounterFhirId = decodedIdToken?.['https://navina.ai/emr_encounter_fhir_id'];
		const emrPatientId = decodedIdToken?.['https://navina.ai/emr_patient_id'];
		const docId = decodedIdToken?.['https://navina.ai/doc_id'];
		const unityPatientId = decodedIdToken?.['https://navina.ai/unity_patient_id'];
		const unityEncounterId = decodedIdToken?.['https://navina.ai/unity_encounter_id'];

		logger.trace('Auth0 token: user logging in from identity provider', {
			dataSourceId: possibleDataSourceIds || dataSourceId,
			email,
			encounterId: epicEmrEncounterId,
			emrEncounterFhirId,
			docId,
		});

		savingTokenRelevantDataToLocalStorage(idToken, decodedIdToken);

		try {
			const trackingEpicFLow = isPartOfEPICLoginFlow();
			if (trackingEpicFLow) {
				addCurrentTimeUnixToLocalStorage(REQUEST_TIME_GET_USER_META_DATA);
			}

			const response = await fetchUserMetadata({
				idToken,
				dataSourceId,
				emrEncounterId: epicEmrEncounterId,
				unityPatientId,
				emrPatientId,
			});

			const { navinaAppointmentId: maybeNavinaAppointmentId, navinaPatientId: maybeNavinaPatientId } = response.data;
			const userMetadata: UserMetadata = {
				analyticsMetadata: response.data.analytics_metadata,
				permissions: response.data.permissions,
				emrUsername: response.data.emr_username_new,
				emrName: response.data.emr_name,
				isAdmin: response.data.is_admin,
				navinaUsername: response.data.navina_username,
				sid: maybeNavinaAppointmentId ?? undefined,
				navinaPatientId: maybeNavinaPatientId,
				navinaAppointmentId: maybeNavinaAppointmentId,
				dataSourceId: response.data?.dataSourceId?.toString() || dataSourceId,
			};

			if (trackingEpicFLow) {
				addCurrentTimeUnixToLocalStorage(RESPONSE_TIME_GET_USER_META_DATA);
			}

			const nextUrl = getEpicLoginNextUrlFromMetadataOrNull({
				maybeNavinaPatientId,
				maybeDocumentId: docId,
				maybeNavinaAppointmentId,
			});

			if (nextUrl) {
				this.refreshEpicFhirAuthCacheIfNeeded(emrEncounterFhirId, maybeNavinaAppointmentId);
				saveNextUrlToLocalStorage(nextUrl);
			}

			saveRelevantUserMetadataToLocalStorage(userMetadata);
			localStorage.setItem(UNITY_ENCOUNTER_ID, unityEncounterId || '');

			callback(userMetadata.emrUsername, null, idToken, userMetadata, true);

			// The callback above runs the SSO login handler synchronously, so the auth token is now in
			// the store. Warm the suggestions request for the known launch context, in parallel with
			// LaunchDarkly init and lazy-chunk loading, so the portrait/summary mount dedupes onto it
			// instead of fetching serially once all feature flags resolve. Imported lazily to avoid a
			// static auth -> suggestions-api -> fetch-client -> auth-store dependency cycle.
			void import('../server/NakamaApis/SuggestionsApi/SuggestionsApi')
				.then(({ prefetchSuggestionsForLaunchContext }) => {
					prefetchSuggestionsForLaunchContext({
						navinaPatientId: maybeNavinaPatientId,
						navinaAppointmentId: maybeNavinaAppointmentId,
					});
				})
				.catch((err: unknown): void =>
					logger.error('Auth0 token: suggestions prefetch failed', { error: getLoggableError(err) }),
				);
		} catch (err) {
			logger.error('Auth0 token: failed to fetch user metadata', { error: getLoggableError(err) });
			callback(null, null, idToken, null, true);
		}
	};

	private readonly handleSuccessfulSignIn = async (
		email: string,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
	): Promise<void> => {
		logger.setUsername(email);
		logger.trace('Sign-in: succeeded', { username: email });
		const session = await fetchAuthSession();
		afterLoginCallback?.(email, session, SignInStatus.success);
	};

	private readonly handleTotpVerification = async (
		email: string,
		verificationCode: string,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
	): Promise<void> => {
		try {
			await confirmSignIn({
				challengeResponse: verificationCode,
			});
			const session = await fetchAuthSession();
			afterLoginCallback?.(email, session, SignInStatus.success);
		} catch (err: any) {
			if (err.name === 'CodeMismatchException' && err.message === 'Invalid code received for user') {
				logger.trace('Sign-in: wrong verification code', { username: email });
			} else {
				logger.error('Sign-in: verification code confirmation failed', {
					username: email,
					error: getLoggableError(err),
				});
			}
			afterLoginCallback?.(null, null, SignInStatus.totpFailure);
		}
	};

	private readonly handleTotpRequired = async (
		email: string,
		verificationCode: string | undefined,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
	): Promise<void> => {
		logger.trace('Sign-in: verification code required', { username: email });
		if (!verificationCode) {
			afterLoginCallback?.(email, null, SignInStatus.totpRequired);
		} else {
			await this.handleTotpVerification(email, verificationCode, afterLoginCallback);
		}
	};

	private readonly handlePasswordChangeRequired = (
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
	): void => {
		logger.trace('Sign-in: new password required on first login');
		afterLoginCallback?.(null, null, SignInStatus.firstLoginPasswordChange);
	};

	private readonly handleSignInError = async (
		err: any,
		email: string,
		password: string,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
		verificationCode?: string,
	): Promise<boolean> => {
		// Password change required (old password)
		if (
			(err.name === 'UserLambdaValidationException' &&
				err.message === 'PreAuthentication failed with error PASSWORD_CHANGE_REQUIRED.') ||
			err.code === 'PasswordResetRequiredException'
		) {
			logger.trace('Sign-in: password change required, password too old', { username: email });
			afterLoginCallback?.(null, null, SignInStatus.oldPassword);
			return true;
		}

		// General error
		if (
			err.name === 'UserLambdaValidationException' &&
			err.message === 'PreAuthentication failed with error GENERAL_ERROR.'
		) {
			logger.error('Sign-in: pre-authentication general error', { username: email, error: getLoggableError(err) });
			afterLoginCallback?.(null, null, SignInStatus.generalError);
			return true;
		}

		// Login too old
		if (
			err.name === 'UserLambdaValidationException' &&
			err.message === 'PreAuthentication failed with error LAST_LOGIN_TOO_OLD.'
		) {
			logger.trace('Sign-in: last login too old', { username: email });
			afterLoginCallback?.(null, null, SignInStatus.oldLogin);
			return true;
		}

		// Password reset required
		if (err.code === 'PasswordResetRequiredException' || err.name === 'Password reset required for the user') {
			logger.trace('Sign-in: password reset required by Cognito', { username: email });
			afterLoginCallback?.(null, null, SignInStatus.firstLoginPasswordChange);
			return true;
		}

		// Incorrect credentials
		if (
			err.message === 'PreAuthentication failed with error INCORRECT_USERNAME_PASSWORD.' ||
			(err.name === 'NotAuthorizedException' && err.message === 'Incorrect username or password.')
		) {
			afterLoginCallback?.(null, null, SignInStatus.credentialsFailure);
			return true;
		}

		// Password attempts exceeded
		if (err.name === 'NotAuthorizedException' && err.message === 'Password attempts exceeded') {
			afterLoginCallback?.(null, null, SignInStatus.passwordAttemptsExceeded);
			return true;
		}

		// User already authenticated
		if (err.name === 'UserAlreadyAuthenticatedException' || err.message === 'There is already a signed in user.') {
			await this.signOutAndRetrySignIn(email, password, afterLoginCallback, verificationCode);
			return true;
		}

		return false;
	};

	private readonly signOutAndRetrySignIn = async (
		email: string,
		password: string,
		afterLoginCallback?: (email: string | null, session: AuthSession | null, status: SignInStatus) => void,
		verificationCode?: string,
	): Promise<void> => {
		logger.trace('Sign-in: user already authenticated, signing out and retrying', { username: email });
		try {
			await signOut();
		} catch (error) {
			logger.error('Sign-in: sign-out failed for already authenticated user', {
				username: email,
				error: getLoggableError(error),
			});
			afterLoginCallback?.(null, null, SignInStatus.failure);
			return;
		}

		logger.trace('Sign-in: signed out existing user, retrying', { username: email });
		try {
			await this.signIn(email, password, afterLoginCallback, verificationCode);
		} catch (error) {
			logger.error('Sign-in: retry failed after signing out already authenticated user', {
				username: email,
				error: getLoggableError(error),
			});
			afterLoginCallback?.(null, null, SignInStatus.failure);
		}
	};

	private readonly removeUrlParam = (paramName: string): void => {
		const currentUrl = new URL(globalThis.location.href);
		currentUrl.searchParams.delete(paramName);
		const newUrl = currentUrl.toString();
		window.history.replaceState(null, '', newUrl);
	};
}
